<template>
  <v-container fluid class="ma-4 px-8">
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="bulkPaymentReportHeaders.slice(0, -1)"
            :items="reportData"
            fixed-header
            hide-default-footer
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.partner_id`]="{ item }">
              {{ getPartnerName(item.partner_id) }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-row class="my-3">
      <span class="ml-5 mt-2 text-5xl font-weight-bold accent--text">
        PAYMENTS
        <span v-if="studentPayments.length"
          >(Total Selected: {{ studentPayments.length }})</span
        >
        <span
          v-if="reportData[0] && reportData[0].finance_check == 'Y'"
          class="ml-2"
        >
          <v-icon small color="green">mdi-check-circle</v-icon>
          <span class="font-weight-bold">Verified</span>
        </span>
      </span>
      <v-spacer></v-spacer>
      <v-btn
        class="primary white--text mr-3"
        :loading="loading"
        v-show="reportData[0] && reportData[0].finance_check != 'Y'"
        :disabled="!studentPayments.length"
        @click="verifyPayments"
      >
        Verify
      </v-btn>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            v-model="studentPayments"
            :headers="computedHeaders"
            :items="reportStudentData"
            item-key="invoice_id"
            show-select
            fixed-header
            selectable-key="isSelectable"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.amount_paid`]="{ item }">
              {{ item.amount_paid ? item.amount_paid : item.amount }}
            </template>
            <template v-slot:[`item.payment_date`]="{ item }">
              {{ formatDate(item.payment_date) }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Confirm Payment{{ studentPayments.length > 1 ? "s" : "" }}
          <v-spacer></v-spacer>
          <v-btn icon large @click="confirmationDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="subtitle-2">
            Do you want to confirm that {{ studentPayments.length }} payment{{
              studentPayments.length > 1 ? "s have" : " has"
            }}
            been received?
          </div>
          <div>
            <v-row class="mt-3" no-gutters>
              <v-col cols="12">
                <b>Transaction Date:</b>
                {{ reportData[0]?.transaction_date || "N/A" }}
              </v-col>
              <v-col cols="12">
                <b>Transaction ID:</b>
                {{ reportData[0]?.transaction_id || "N/A" }}
              </v-col>
              <v-col cols="12">
                <b>Amount:</b> ₹{{ reportData[0]?.amount || "N/A" }}
              </v-col>
            </v-row>
          </div>

          <div>
            <v-row class="mt-3" no-gutters>
              <v-col cols="6" class="pt-3">
                <v-menu
                  v-model="showDatePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="realizationDateDisplay"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      label="Realization Date*"
                      outlined
                      dense
                      hide-details="auto"
                      :rules="[
                        (v) =>
                          !!realization_date || 'Realization date is required',
                      ]"
                    />
                  </template>
                  <v-date-picker
                    v-model="realization_date"
                    @input="showDatePicker = false"
                  />
                </v-menu>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-actions class="mt-n3">
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!realization_date"
            :loading="loading"
            @click="updatePaymentStatus()"
          >
            Verify
            {{
              studentPayments.length > 1 ? `(${studentPayments.length})` : ""
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  bulkPaymentReportHeaders,
  bulkPaymentStudentHeaders,
} from "@/constants/headers";

export default {
  data() {
    return {
      reportData: [],
      reportStudentData: [],
      bulkPaymentReportHeaders,
      bulkPaymentStudentHeaders,
      partners: [],
      loading: false,
      bulk_payment_id: null,
      payment_type: "",
      studentPayments: [],
      studentPaymentIds: [],
      confirmationDialog: false,
      realization_date: null,
      showDatePicker: false,
    };
  },
  computed: {
    computedHeaders() {
      const headers = [];

      if (this.payment_type === "Admission") {
        headers.push(
          {
            text: "Application ID",
            value: "application_id",
            class: "black--text font-weight-bold secondary",
          },
          ...this.bulkPaymentStudentHeaders,
        );
      } else {
        headers.push(
          {
            text: "USN",
            value: "usn_id",
            class: "black--text font-weight-bold secondary",
          },
          ...this.bulkPaymentStudentHeaders,
        );
      }

      return headers;
    },

    selectedItem() {
      return this.studentPayments.length === 1 ? this.studentPayments[0] : null;
    },

    maxDate() {
      if (!this.reportData[0]?.transaction_date) return "";
      return this.formatDate(this.reportData[0].transaction_date);
    },

    realizationDateDisplay() {
      if (!this.reportData[0]?.transaction_date) return "";
      return this.formatDate(this.realization_date);
    },

    formatPaymentDate() {
      if (!this.reportData[0]?.transaction_date) return "";
      return this.formatDate(this.reportData[0].transaction_date);
    },
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return "";
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    },

    async bulkPayments() {
      this.loading = true;
      await this.$axios
        .get(`/business_partner_bulk_payments/${this.bulk_payment_id}`)
        .then((res) => {
          this.reportData = [res.data.data];
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async bulkStudentPayments() {
      this.loading = true;
      let url = null;

      if (this.payment_type == "Admission") {
        url = "/list_student_admit_plan_bulk_payments";
      } else {
        url = "/list_student_invoices_report";
      }
      await this.$axios
        .get(url, {
          params: { bulk_payment_id: this.bulk_payment_id },
        })
        .then((res) => {
          this.reportStudentData = res.data.data;
          this.loading = false;
        })
        .catch((error) => {
          console.log(error);
        });
    },

    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    getPartnerName(id) {
      const partner = this.partners.find((partner) => partner.id === id);
      return partner ? partner.name : "";
    },

    verifyPayments() {
      if (!this.studentPayments.length) {
        this.$toast.error("Please select at least one payment");
        return;
      }

      this.confirmationDialog = true;
      this.studentPaymentIds = this.studentPayments.map((p) => p.invoice_id);

      // Set default realization date to the earliest payment date
      if (this.maxDate) {
        this.realization_date = this.maxDate;
      }
    },

    async updatePaymentStatus() {
      if (!this.realization_date) {
        this.$toast.error("Please select a realization date");
        return;
      }

      this.loading = true;

      try {
        await this.$axios.put("/update_payment_by_cfo", {
          ids: this.studentPaymentIds,
          realization_date: this.realization_date,
          filter_by:
            this.payment_type == "Admission"
              ? "application"
              : "student_invoice",
        });

        await this.bulkPayments();
        await this.bulkStudentPayments();

        this.confirmationDialog = false;
        this.studentPayments = [];
        this.realization_date = null;
      } catch (error) {
        console.error(error);
        this.$toast.error("Failed to verify payments");
      } finally {
        this.loading = false;
      }
    },
  },

  async created() {
    this.bulk_payment_id = this.$route.query?.id;
    this.payment_type = this.$route.query?.payment_type;

    if (!this.bulk_payment_id || !this.payment_type) {
      this.$router.go(-1);
      return;
    }

    await Promise.all([this.getPartners(), this.bulkPayments()]);

    this.$store.commit("setBreadcrumbs", [
      { text: "Transaction Verification", to: { name: "Transaction Verification" } },
      { text: "Bulk Payments Verification" },
    ]);
    this.bulkStudentPayments();
  },
};
</script>

<style scoped>
ul {
  padding-left: 20px;
  margin-top: 5px;
  margin-bottom: 5px;
}
li {
  font-size: 0.9rem;
}
</style>
